import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { useNavigate, useLocation, useParams } from "react-router";
import { FormattedMessage } from "react-intl";
import { PrimaryButton, SecondaryButton } from "components/Buttons";
import { useAuth, useLinks, useLogout } from "hooks";
import { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { AdvancedButton } from "components/Buttons";
import { Link } from "components/Links";
import { colors, fontSize } from "style";

/**
 * HeaderAuth
 *
 * @param {Array} pages
 */
const HeaderAuth = ({ pages = [] }) => {
  const { isAuthenticated, isImpersonating, firstName } = useAuth();
  const { handleLogout } = useLogout();
  const navigate = useNavigate();
  const links = useLinks();

  const HamburgerMenu = () => (
    <div css={styles.navToggle} data-hamburger-menu>
      <span css={styles.name}>
        <FormattedMessage
          id={isImpersonating ? "Menu.Impersonating" : "Menu.Name"}
          values={{
            name: <b>{firstName}</b>,
          }}
        />
      </span>
      <AdvancedButton
        iconType={ICON_TYPE.listLines}
        iconSize={ICON_SIZE.xlarge}
        options={[
          ...pages.map((page) => ({
            label: (
              <Link to={page.to} css={styles.link} routerEnabled>
                {page.label}
              </Link>
            ),
          })),
          {
            label: (
              <Link onClick={() => handleLogout(() => navigate(links.login))} css={styles.link}>
                <FormattedMessage id="Global.Logout" />
              </Link>
            ),
          },
        ]}
      />
    </div>
  );

  const ButtonMenu = () => {
    const { id, missionId } = useParams();
    const { search, pathname } = useLocation();
    let rolePath = "";

    if (pathname === links.public_mission_role(missionId, id)) {
      rolePath = `${search ? "&" : "?"}role=${id}`;
    }

    return (
      <div css={styles.buttons} data-button-menu>
        <a href={`${links.login}?redirect=${pathname}`}>
          <SecondaryButton>
            <FormattedMessage id="Global.Login" />
          </SecondaryButton>
        </a>
        <a href={`${links.signup}${search}${rolePath}`}>
          <PrimaryButton>
            <FormattedMessage id="Global.JoinMission" />
          </PrimaryButton>
        </a>
      </div>
    );
  };

  return isAuthenticated ? <HamburgerMenu /> : <ButtonMenu />;
};

const styles = {
  navToggle: css`
    display: flex;
    align-items: center;
    gap: 1rem;

    button {
      background-color: transparent;
      color: ${colors.grayAnatomyDark};
    }

    ul > div {
      padding: 0;
    }
  `,
  name: css`
    color: ${colors.grayAnatomyDark};
    font-family: Matter;
    font-size: ${fontSize.small};
    font-weight: 400;
    line-height: 1;
    display: block;
    margin-bottom: -2px;
  `,
  buttons: css`
    display: flex;
    justify-content: flex-end;
    gap: 1.6rem;
  `,
  link: css`
    padding: 1.2rem 1.6rem;
    font-family: Matter;
    font-size: ${fontSize.small};
    font-weight: 500;
    line-height: 1.33;
    display: block;
    color: ${colors.purpleRainDark2};
    width: 100%;

    &:hover {
      background-color: ${colors.grayAnatomyLight5};
      color: ${colors.purpleRainDark2};
    }
  `,
};

HeaderAuth.propTypes = {
  pages: PropTypes.array,
};

export default HeaderAuth;
